import React, { useState } from "react";
import { Form, Modal } from "react-bootstrap";

import DstButton from "../../../../components/DstButton/DstButton";
import MultiSelectLiveDropdown from "../../../../components/MultiSelectLiveDropdown/MultiSelectLiveDropdown";
import Popup from "../../../../components/Popup/Popup";
import RequiredLabel from "../../../../components/RequiredLabel/RequiredLabel";
import { useSession } from "../../../../contexts/SessionContext";
import translate from "../../../../services/Translate";
import { CursusCreationFormState, CursusCreationModalProps } from "./CreationModal.d";
import { createCursus } from "./CreationModal.function";

const initialForm: CursusCreationFormState = {
  name: "",
};

const CursusCreationModal = ({
  language,
  setShowCreationModal,
  showCreationModal,
  onSuccess,
}: CursusCreationModalProps) => {
  const { showAlert, popAlert } = useSession();
  const [form, setForm] = useState<CursusCreationFormState>(initialForm);
  const [loading, setLoading] = useState(false);

  const _updateForm = (fields: Partial<CursusCreationFormState>) => setForm((prev) => ({ ...prev, ...fields }));
  const resetSelection = () => setForm(initialForm);

  const _handleConfirm = () => {
    if (!showAlert || !popAlert) return;
    createCursus(
      {
        name: form.name.trim(),
        owner_id: form.selectedOwner?.id,
        description: form.description?.trim() || null,
        school_id: form.school ?? null,
      },
      language,
      setLoading,
      showAlert,
      popAlert,
      setShowCreationModal,
      resetSelection,
      onSuccess
    );
  };

  const isFormValid = !!form.name.trim() && !!form.selectedOwner;

  return (
    <React.Fragment>
      <Popup
        show={showCreationModal}
        onClose={() => setShowCreationModal(false)}
        modalTitle={translate(language, "PAGES.COURSES.MODAL.TITLE")}
        scrollable
      >
        <Form id="modal-add-cursus">
          <Form.Group>
            <Form.Label>
              <RequiredLabel label={translate(language, "GLOBAL.NAME")} />
            </Form.Label>
            <Form.Control
              className={`border-secondary ${!form.name ? "text-muted" : ""}`}
              type="text"
              maxLength={255}
              placeholder={translate(language, "PAGES.COURSES.MODAL.PLACEHOLDERS.NAME")}
              value={form.name}
              onChange={(event) => _updateForm({ name: event.target.value })}
            />
          </Form.Group>

          <Form.Group className="mt-3">
            <Form.Label>{translate(language, "GLOBAL.DESCRIPTION")}</Form.Label>
            <Form.Control
              className={`border-secondary ${!form.description ? "text-muted" : ""}`}
              as="textarea"
              rows={3}
              placeholder={translate(language, "PAGES.COURSES.MODAL.PLACEHOLDERS.DESCRIPTION")}
              value={form.description ?? ""}
              onChange={(event) => _updateForm({ description: event.target.value })}
            />
          </Form.Group>

          <Form.Group className="mt-3">
            <Form.Label>{translate(language, "PAGES.COURSES.MODAL.LABELS.SCHOOL")}</Form.Label>
            <Form.Select
              className={`border-secondary ${!form.school ? "text-muted" : ""}`}
              value={form.school ?? ""}
              onChange={(event) => _updateForm({ school: Number(event.target.value) || undefined })}
            >
              <option value="">{translate(language, "PAGES.COURSES.MODAL.PLACEHOLDERS.SELECT_SCHOOL")}</option>
              <option value={1}>Datascientest</option>
              <option value={2}>CyberUniversity</option>
            </Form.Select>
          </Form.Group>

          <Form.Group className="mt-3">
            <Form.Label>
              <RequiredLabel label={translate(language, "PAGES.COURSES.MODAL.LABELS.OWNER")} />
            </Form.Label>
            <MultiSelectLiveDropdown
              label={form.selectedOwner?.name ?? translate(language, "PAGES.COURSES.MODAL.PLACEHOLDERS.SELECT_OWNER")}
              category={["users"]}
              language={language}
              isSingleSelect
              onConfirm={(selected) =>
                _updateForm({
                  selectedOwner:
                    selected.length > 0
                      ? {
                          id: +selected[0].value.id,
                          name: `${selected[0].value.first_name} ${selected[0].value.last_name}`,
                        }
                      : undefined,
                })
              }
              toggleClass={`text-truncate w-100 border-secondary ${!form.selectedOwner ? "text-muted" : ""}`}
            />
          </Form.Group>
        </Form>

        <Modal.Footer>
          <DstButton
            variant="secondary"
            value={translate(language, "GLOBAL.CANCEL")}
            clickFunction={() => {
              setShowCreationModal(false);
              resetSelection();
            }}
          />
          <DstButton
            value={translate(language, "GLOBAL.CREATE")}
            clickFunction={_handleConfirm}
            disabled={!isFormValid || loading}
            loading={loading}
          />
        </Modal.Footer>
      </Popup>
    </React.Fragment>
  );
};

export default CursusCreationModal;
